import React, { useCallback, useMemo, useState } from 'react';
import { Modal, Input } from 'antd';
import { useDispatch } from 'react-redux';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { CampaignMembersTable } from '@components/Tables';
import { toggleCampaignMemberGroupModal } from '@redux/actions/modalAction';
import { useTypedSelector } from '@hooks';
import { getMembersList } from '@api/Campaign';
import { InfiniteScroll } from '@components/InfiniteScroll/InfiniteScroll';
import { useDebounced } from '@helpers/useDebounced';
import { CampaignMemberGroupModalStateProps } from '@redux/types/modalType';
import { CrossIcon, SearchIcon } from '@icons';

function CampaignMemberGroupModal() {
  const modalState = useTypedSelector(
    ({ modal }) => modal.campaignMemberGroupModal
  );
  if (!modalState) return null;
  return <CampaignMemberGroupModalInner {...modalState} key={modalState.key} />;
}

function CampaignMemberGroupModalInner({
  campaign,
  visible,
  group
}: CampaignMemberGroupModalStateProps) {
  const dispatch = useDispatch();

  const [membersSearchQuery, setMembersSearchQuery] = useState<string>('');
  const debouncedMembersSearchQuery = useDebounced(membersSearchQuery, 250);
  const [membersSort, setMembersSort] = useState<
    'default:ASC' | 'default:DESC' | 'role:ASC' | 'role:DESC'
  >('default:ASC');

  const membersQuery = useInfiniteQuery({
    queryKey: [
      'campaign',
      'members',
      'list',
      campaign.id,
      group.id,
      debouncedMembersSearchQuery,
      membersSort
    ],
    keepPreviousData: true,
    enabled: visible,
    queryFn: async ({ pageParam, signal }) => {
      const { data } = await getMembersList(
        {
          campaignId: campaign.id,
          groupId: group.id,
          searchQuery: debouncedMembersSearchQuery,
          after: pageParam,
          orderBy: [membersSort]
        },
        { signal }
      );
      return data;
    },
    getNextPageParam: (lastPage) =>
      lastPage.hasNext ? lastPage.endCursor : undefined
  });
  const membersList = useMemo(
    () =>
      membersQuery.data?.pages?.flatMap((x) => x.edges).map((x) => x.node) ??
      [],
    [membersQuery.data]
  );

  const handleSort = useCallback((value: string) => {
    switch (value) {
      case 'role:ASC':
      case 'role:DESC': {
        setMembersSort(value);
        break;
      }
      default: {
        setMembersSort('default:ASC');
      }
    }
  }, []);

  return (
    <Modal
      open={visible}
      destroyOnClose
      centered={true}
      closeIcon={<CrossIcon />}
      width={1000}
      zIndex={1005}
      onCancel={() => dispatch(toggleCampaignMemberGroupModal(null))}
      className="members-modal group-modal"
      footer={null}
    >
      <div className="modal_container">
        <div className="group-name-input" style={{ marginBottom: 20 }}>
          <Input size="large" value={group.name} readOnly />
        </div>
        <div style={{ display: 'flex', gap: 24, marginBottom: 20 }}>
          <Input
            placeholder="Search by name or email"
            prefix={<SearchIcon />}
            size="large"
            style={{ width: '100%', borderRadius: 8 }}
            value={membersSearchQuery}
            onChange={(e) => setMembersSearchQuery(e.target.value)}
          />
        </div>
        <div className="modal_container__workspace_members">
          <OverlayScrollbarsComponent
            className="table_container"
            style={{ marginRight: -20 }}
          >
            <InfiniteScroll
              loading={membersQuery.isFetchingNextPage ? 'more' : false}
              next={membersQuery.fetchNextPage}
              hasMore={membersQuery.hasNextPage ?? false}
            >
              <CampaignMembersTable
                onChangeOrderBy={handleSort}
                userList={membersList}
                isLoading={
                  membersQuery.isLoading ||
                  membersQuery.isPreviousData ||
                  membersQuery.isFetchingNextPage
                }
              />
            </InfiniteScroll>
          </OverlayScrollbarsComponent>
        </div>
      </div>
    </Modal>
  );
}

export default CampaignMemberGroupModal;
